/**
 * Generic error message for unknown component errors.
 * @param {string} component_name The name of the component throwing the error.
 * @returns {string}
 */
export const COMPONENT_UNKNOWN_FAILURE = (component_name = 'Component'): string =>
  `Error: ${component_name} failed on an unknown error while initialising.`;

/**
 * Generic error message for unknown function errors.
 * @param {string} source The source file or functional component throwing the error.
 * @param {string} function_name The name of the function causing the error.
 * @returns {string}
 */
export const FUNCTION_UNKNOWN_FAILURE = (source = 'file.ts', function_name = 'function'): string =>
  `Error: ${source} failed on an unknown error while calling ${function_name}.`;

/**
 * Generic error message to be displayed to the user.
 * @param {string} tracking_id The error id.
 * @returns {string}
 */
export const FEEDBACK_UNKNOWN_FAILURE = (tracking_id = 'unknown_id'): string =>
  `An error occurred. Tracking ID: ${tracking_id}.`;

/**
 * Generic error message to be displayed to the user, specifying that the application was unable to complete some action.
 * @param {string} message The action specific error to display.
 * @param {string} tracking_id The error id.
 * @returns {string}
 */
export const FEEDBACK_ACTION_FAILURE = (
  message = 'Unable to execute action',
  tracking_id = 'unknown_id'
): string => `${message}. Tracking ID: ${tracking_id}.`;

/**
 * Generic error message for operations performed on a company or group level while not provided with the necessary parameters.
 * @param {Array<string>} entity_names List of entities required for non-master_list operation.
 * @returns {string}
 */
export const ENTITIES_REQUIRED_NON_MASTERLIST = (
  entity_names = ['group_id', 'company_id']
): string => `${entity_names.concat(', ')} are required for non-masterlist operations.'`;

/**
 * Generic failure message for various data job service executions.
 * @param {string} data_job_type Display name of data job type that failed.
 * @returns {string}
 */
export const DATA_JOB_FAILURE = (data_job_type = 'Data Job'): string => `${data_job_type} failed.`;

/**
 * Generic completion message for various data job service executions.
 * @param {string} data_job_type Display name of data job type that finished.
 * @returns {string}
 */
export const DATA_JOB_COMPLETE = (data_job_type = 'Data Job'): string =>
  `${data_job_type} completed.`;

/**
 * Generic error message for service layer functions returning no data.
 * @param {string} function_name The name of the function causing the error.
 * @returns {string}
 */
export const FUNCTION_NO_DATA_FAILURE = (function_name = 'function'): string =>
  `Error: Missing / unexpected data returned from calling ${function_name}.`;
